import { Cpu, RefreshCw, Trash2, X, Zap } from 'lucide-react';
import { DiagnosticEntry } from '../api/client';
import { cn } from '../lib/utils';

const typeColors: Record<string, string> = {
  metric: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  diagnostic: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

function formatPayload(payload?: string): string {
  if (!payload) return 'No payload data';
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

export default function DiagnosticDetailModal({
  entry,
  loading,
  onClose,
  onDelete,
}: {
  entry: DiagnosticEntry | null;
  loading: boolean;
  onClose: () => void;
  onDelete?: (id: string) => void;
}) {
  if (!entry && !loading) return null;

  return (
    <div
      className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal-content max-w-3xl w-full max-h-[80vh] flex flex-col rounded-2xl animate-scale-in"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Diagnostic report detail"
      >
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        ) : entry ? (
          <>
            <div className="flex items-center justify-between p-6 border-b border-border/50">
              <div className="flex items-center gap-3 min-w-0">
                <span className={cn(
                  'inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium rounded-full border flex-shrink-0',
                  typeColors[entry.payload_type] || 'bg-gray-500/20 text-gray-400'
                )}>
                  {entry.payload_type === 'diagnostic' ? (
                    <Zap className="w-3.5 h-3.5" />
                  ) : (
                    <Cpu className="w-3.5 h-3.5" />
                  )}
                  {entry.payload_type}
                </span>
                <span className="text-sm text-gray-400 truncate">
                  {entry.device_model || 'Unknown device'} &middot; v{entry.app_version || 'unknown'} &middot; iOS {entry.ios_version || 'unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {onDelete && (
                  <button
                    onClick={() => onDelete(entry.id)}
                    className="text-gray-500 hover:text-rose-400 transition p-1.5"
                    title="Delete"
                    aria-label="Delete diagnostic report"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-300 transition p-1.5"
                  aria-label="Close diagnostic report"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="text-xs text-gray-500 mb-3">
                ID: {entry.id} &middot; Received: {new Date(entry.created_at).toLocaleString()}
                {entry.device_id && <> &middot; Device: {entry.device_id}</>}
              </div>
              <pre className="text-xs text-gray-300 bg-black/30 rounded-xl p-4 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
                {formatPayload(entry.payload_json)}
              </pre>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
